/**
 * Aufträge abrechnen.
 *
 * **Wann taucht eine Tour hier auf?** Wenn sie gefahren ist — also alle Fahrten
 * abgeschlossen sind — und der Fahrer die Papiere in der App gespeichert hat.
 * In der Disposition steht dann „Belege verfügbar". Genau diese Touren stehen
 * hier oben.
 *
 * Touren, bei denen noch ein Beleg fehlt, stehen darunter in einem eigenen
 * Block. Sie zu verstecken wäre bequem und falsch: Wer eine Woche abrechnet,
 * muss sehen, was fehlt, bevor er die Rechnung schreibt — nicht danach.
 *
 * **Wo trägt man die Rechnungsnummer ein?** In der Zeile, rechts. Es gibt
 * keinen Statusweg mehr davor. Die Nummer ist die eine Sperre des Systems:
 * Sobald sie steht, kann niemand mehr etwas ändern, auch der Fahrer nicht.
 *
 * Gruppiert wird nach Kunde, weil bei Haid & Jerg wöchentlich je Kunde
 * abgerechnet wird. Die Summe je Gruppe ist die Zahl, die auf die Rechnung
 * kommt.
 *
 * ═══ DER WEG ZUR RECHNUNG ═══
 *
 * Preis und Preisart je Tour eintragen, dann je Kunde eine CSV ausgeben. In
 * SevDesk entsteht daraus eine Rechnung über den Gesamtbetrag, die CSV geht
 * als Leistungsnachweis mit. Alle Preise sind NETTO — die Umsatzsteuer rechnet
 * SevDesk.
 *
 * Die Datei enthält nur Datenzeilen, keine Summenzeile. Eine Summe mitten in
 * einer Tabelle bricht jede maschinelle Weiterverarbeitung, und der Betrag
 * steht ohnehin auf der Rechnung — und hier auf dem Bildschirm direkt neben
 * dem Knopf. Wer prüfen will, ob beides übereinstimmt, sieht es in derselben
 * Zeile.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Seitenkopf } from '../Rahmen';
import {
  Fahrernotizblock,
  Fahrernotizzeichen,
  Fehlerkasten,
  Karte,
  Knopf,
  Ladehinweis,
  Marker,
  Meldung,
  Textfeld,
  Zahlfeld,
} from '../gestaltung/bausteine';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import { alsCsv, dateiname, herunterladen } from '../daten/ausgabe';
import { kundenLaden } from '../daten/stammdaten';
import { zahlAlsText } from '../daten/zahl';
import {
  abrechnungLaden,
  beleglageText,
  betrag,
  euro,
  fahrernotiz,
  mengenmarke,
  ohneBetrag,
  preisSetzen,
  PREISARTTEXT,
  rechnungsnummerSetzen,
  rechnungsnummerSetzenViele,
  summeAufCent,
  type Auftragszeile,
} from '../daten/auftraege';

/** Eine Karte der Seite: alle Touren EINES Kunden. */
type Gruppe = { schluessel: string; bezeichnung: string; zeilen: Auftragszeile[] };

export function AbrechnungSeite() {
  const [bereich, setBereich] = useState<'offen' | 'abgerechnet'>('offen');
  const alle = useAbfrage(abrechnungLaden, []);
  // Für Anzeigename und Ort in der Überschrift — die Übersicht kennt nur den
  // Namen. Scheitert das Laden, steht der Name der Zeile da; die Gruppierung
  // hängt nicht daran.
  const kundenstamm = useAbfrage(kundenLaden, []);
  const gehZu = useNavigate();

  const [fehler, setFehler] = useState<Klarfehler | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const { bereit, wartetAufFreigabe, erledigt } = useMemo(() => {
    const zeilen = alle.daten ?? [];
    return {
      // Nur, was das Büro freigegeben hat. Vorher füllte sich diese Liste von
      // selbst — und stand dann voller Touren, die man überspringen musste.
      // Nach der dritten übersprungenen Zeile überspringt man die Liste.
      bereit: zeilen.filter((a) => a.zustand === 'abrechenbar'),
      // Was so weit wäre, aber noch niemand freigegeben hat. Es steht darunter,
      // nicht dazwischen: Sonst wäre die Freigabe wieder nur Zeremonie.
      wartetAufFreigabe: zeilen.filter(
        (a) => a.zustand === 'abrechnungsreif' || a.zustand === 'belege_fehlen',
      ),
      erledigt: zeilen.filter((a) => a.zustand === 'abgerechnet'),
    };
  }, [alle.daten]);

  async function mitFehlern(schluessel: string, tun: () => Promise<void>) {
    setFehler(null);
    setBeschaeftigt(schluessel);
    try {
      await tun();
      alle.auffrischen();
    } catch (problem) {
      setFehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setBeschaeftigt(null);
    }
  }

  const gezeigt = bereich === 'offen' ? bereit : erledigt;

  /**
   * Der Leistungsnachweis eines Kunden: eine Zeile je Tour.
   *
   * Je Tour und nicht je Fuhre — das ist die Ebene, auf der der Kunde seine
   * eigenen Lieferscheine abgleicht. Bei Pendelverkehr steht die Fuhrenzahl in
   * der Mengenspalte; zwanzig Einzelzeilen für einen Tag am selben Werk würden
   * den Abgleich erschweren statt ihn zu erleichtern.
   *
   * Die Auftragsnummer des Kunden steht mit drin. Sie ist die Spalte, über die
   * er die Zeile in seinem System wiederfindet — ohne sie muss er über Datum
   * und Ort suchen.
   */
  function nachweisAusgeben(kunde: string, zeilen: Auftragszeile[]) {
    herunterladen(
      dateiname('Leistungsnachweis', kunde, new Date()),
      alsCsv(zeilen, [
        { kopf: 'Datum', wert: (z) => z.ladedatum ?? '' },
        { kopf: 'Tour', wert: (z) => z.nummer ?? '' },
        { kopf: 'Ihre Auftragsnummer', wert: (z) => z.externe_nummer ?? '' },
        { kopf: 'Von', wert: (z) => z.ladeort ?? '' },
        { kopf: 'Nach', wert: (z) => z.entladeort ?? '' },
        { kopf: 'Material', wert: (z) => z.material ?? '' },
        { kopf: 'Fuhren', wert: (z) => (z.fuhren > 0 ? z.fuhren : '') },
        { kopf: 'Menge', wert: (z) => z.menge, zahl: true },
        { kopf: 'Einheit', wert: (z) => z.mengeneinheit ?? '' },
        { kopf: 'Abrechnungsart', wert: (z) => PREISARTTEXT[z.preisart ?? ''] ?? '' },
        { kopf: 'Einzelpreis netto EUR', wert: (z) => z.preis_vk, zahl: true },
        { kopf: 'Betrag netto EUR', wert: (z) => betrag(z), zahl: true },
      ]),
    );
  }

  /**
   * ═══ GRUPPIERT WIRD NACH KUNDE, NICHT NACH KUNDENNAME ═══
   *
   * Der Schlüssel war der Name. Zwei Kunden desselben Namens — zwei Standorte,
   * beide „Korn Recycling" — landeten in EINER Karte: gemeinsame Summe,
   * gemeinsamer Leistungsnachweis, und „Alle N abschließen" schrieb EINE
   * Rechnungsnummer an Touren beider Kunden. Danach ist alles gesperrt, und die
   * Nummer lässt sich nicht mehr korrigieren.
   *
   * Der Schlüssel ist jetzt die Kennung. Der Name dient nur der Anzeige — und
   * mit dem Ort dahinter, damit man die beiden Karten auseinanderhält.
   */
  const gruppen = useMemo(() => {
    const stamm = new Map((kundenstamm.daten ?? []).map((k) => [k.id, k]));
    const nachKunde = new Map<string, Gruppe>();
    for (const zeile of gezeigt) {
      const schluessel = zeile.kunde_id ?? 'ohne-kunde';
      let gruppe = nachKunde.get(schluessel);
      if (!gruppe) {
        const kunde = zeile.kunde_id ? stamm.get(zeile.kunde_id) : undefined;
        const name = kunde?.anzeigename || kunde?.name || zeile.kunde || '— ohne Kunde —';
        gruppe = {
          schluessel,
          bezeichnung: kunde?.ort ? `${name} · ${kunde.ort}` : name,
          zeilen: [],
        };
        nachKunde.set(schluessel, gruppe);
      }
      gruppe.zeilen.push(zeile);
    }
    return [...nachKunde.values()].sort((a, b) =>
      a.bezeichnung.localeCompare(b.bezeichnung, 'de'),
    );
  }, [gezeigt, kundenstamm.daten]);

  return (
    <>
      <Seitenkopf
        titel="Aufträge abrechnen"
        erklaerung="Hier steht, was gefahren ist und wofür die Belege da sind. Preis eintragen, Rechnungsnummer eintragen, fertig — einen Statusweg gibt es nicht mehr."
        werkzeuge={
          <div className="umschalter">
            <button
              className={bereich === 'offen' ? 'aktiv' : undefined}
              onClick={() => setBereich('offen')}
            >
              Abrechenbar ({bereit.length})
            </button>
            <button
              className={bereich === 'abgerechnet' ? 'aktiv' : undefined}
              onClick={() => setBereich('abgerechnet')}
            >
              Abgerechnet
            </button>
          </div>
        }
      />

      {fehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={fehler} />
        </div>
      )}

      {/* Auch auf den Kundenstamm warten: Sonst steht erst der Name da und
          springt einen Augenblick später auf „Name · Ort" um — bei zwei
          Standorten desselben Betriebs sähe man kurz zwei gleiche Karten. */}
      {(alle.laedt && !alle.daten) ||
      (kundenstamm.laedt && !kundenstamm.daten && !kundenstamm.fehler) ? (
        <Ladehinweis />
      ) : alle.fehler ? (
        <Fehlerkasten fehler={alle.fehler} nochmal={alle.auffrischen} />
      ) : (
        <div className="stapel" style={{ gap: 16 }}>
          {gruppen.length === 0 ? (
            <Meldung art="gut" titel="Nichts offen">
              {bereich === 'offen'
                ? 'Keine Tour ist gefahren und belegvollständig. Was noch unterwegs ist, steht in der Disposition.'
                : 'Es wurde noch nichts abgerechnet.'}
            </Meldung>
          ) : (
            gruppen.map((gruppe) => (
              <Kundengruppe
                key={gruppe.schluessel}
                kunde={gruppe.bezeichnung}
                zeilen={gruppe.zeilen}
                abgerechnet={bereich === 'abgerechnet'}
                ausgeben={() => nachweisAusgeben(gruppe.bezeichnung, gruppe.zeilen)}
                blockAbschliessen={(ids, nummer) =>
                  void mitFehlern(`block:${gruppe.schluessel}`, () =>
                    rechnungsnummerSetzenViele(ids, nummer).then(() => undefined),
                  )
                }
                beschaeftigt={beschaeftigt}
                oeffnen={(a) => gehZu(`/auftrag/${a.id}`)}
                preisAendern={(zeile, werte) =>
                  void mitFehlern(`preis:${zeile.id}`, () =>
                    preisSetzen(zeile, werte),
                  )
                }
                nummerSetzen={(zeile, nummer) =>
                  void mitFehlern(`nummer:${zeile.id}`, () =>
                    rechnungsnummerSetzen(zeile, nummer),
                  )
                }
              />
            ))
          )}

          {bereich === 'offen' && wartetAufFreigabe.length > 0 && (
            <Karte
              titel={
                <span className="reihe" style={{ gap: 10 }}>
                  <span>Gefahren, noch nicht freigegeben</span>
                  <Marker art="warn">{wartetAufFreigabe.length}</Marker>
                </span>
              }
            >
              <div className="stapel" style={{ gap: 8 }}>
                <p className="meta">
                  Diese Touren sind gefahren. Sie erscheinen oben, sobald du sie
                  in der Detailsicht zur Abrechnung freigibst — bei manchen
                  fehlt noch ein Beleg, bei anderen nur die Entscheidung.
                </p>
                {wartetAufFreigabe.map((a) => {
                  const lage = beleglageText(a.beleglage);
                  return (
                    <button
                      key={a.id}
                      className="wahlzeile"
                      onClick={() => gehZu(`/auftrag/${a.id}`)}
                    >
                      <span>
                        <strong>{a.nummer ?? 'ohne Nummer'}</strong> · {a.kunde} ·{' '}
                        {a.ladeort} → {a.entladeort}
                      </span>
                      {/* Grün wie in der Disposition (HJ-031): Hier fehlt nur
                          noch die Entscheidung, und genau das soll auffallen. */}
                      <Marker art={a.zustand === 'abrechnungsreif' ? 'gut' : 'warn'}>
                        {a.zustand === 'abrechnungsreif'
                          ? 'wartet auf Freigabe'
                          : lage.text}
                      </Marker>
                    </button>
                  );
                })}
              </div>
            </Karte>
          )}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------- Gruppe je Kunde */

function Kundengruppe(eigenschaften: {
  kunde: string;
  zeilen: Auftragszeile[];
  abgerechnet: boolean;
  ausgeben: () => void;
  blockAbschliessen: (auftragIds: string[], nummer: string) => void;
  beschaeftigt: string | null;
  oeffnen: (zeile: Auftragszeile) => void;
  preisAendern: (
    zeile: Auftragszeile,
    werte: {
      preisart?: string | null;
      preis_vk?: number | null;
      mengeneinheit?: string | null;
      abrechnungsmenge?: number | null;
    },
  ) => void;
  nummerSetzen: (zeile: Auftragszeile, nummer: string) => void;
}) {
  // Summe aus den Centbeträgen der Zeilen — dieselben, die in der CSV stehen.
  // Vorher addierte der Bildschirm ungerundete Werte und die Datei rundete je
  // Zeile: Summe und Datei konnten um Cent auseinanderliegen.
  const summe = summeAufCent(
    eigenschaften.zeilen.map((z) => betrag(z) ?? 0),
  );
  // ═══ „OHNE BETRAG", NICHT „OHNE PREIS" ═══
  //
  // Eine Tour mit Preis, aber ohne Menge (oder ohne Preisart) hat keinen
  // Betrag. Wer nur den Preis prüfte, ließ sie in den Leistungsnachweis — mit
  // leerer Betragszelle, und die Summe neben dem Knopf war zu klein. Der
  // Stundensatz zählt nicht: Dort gibt es bewusst keine Zahl am Auftrag.
  const ohnePreis = eigenschaften.zeilen.filter((z) => ohneBetrag(z)).length;

  const [blockOffen, setBlockOffen] = useState(false);
  const [blocknummer, setBlocknummer] = useState('');
  const offeneIds = eigenschaften.zeilen
    .filter((z) => z.rechnungsnummer === null)
    .map((z) => z.id);

  return (
    <Karte
      titel={
        <span className="reihe" style={{ gap: 10 }}>
          <span>{eigenschaften.kunde}</span>
          <Marker art="neutral">{eigenschaften.zeilen.length}</Marker>
          {ohnePreis > 0 && <Marker art="warn">{ohnePreis} ohne Betrag</Marker>}
        </span>
      }
      werkzeuge={
        <div className="reihe" style={{ gap: 12, alignItems: 'center' }}>
          {/* Die Summe steht NEBEN dem Ausgabeknopf, nicht irgendwo weiter
              oben: Genau hier vergleicht man sie mit dem Betrag, den man
              gleich in SevDesk eintippt. */}
          <span style={{ fontWeight: 700, fontSize: 17 }}>{euro(summe)}</span>
          <span className="meta">netto</span>
          <Knopf
            art="zweit"
            klein
            disabled={ohnePreis > 0}
            title={
              ohnePreis > 0
                ? 'Erst bei allen Touren Preis und Menge eintragen — sonst fehlen Beträge im Nachweis.'
                : 'Eine Zeile je Tour, für den Anhang an die Rechnung.'
            }
            onClick={eigenschaften.ausgeben}
          >
            Leistungsnachweis als CSV
          </Knopf>

          {/* ═══ EINE NUMMER FÜR DEN GANZEN BLOCK ═══

              Abgerechnet wird, wie abgerechnet wird: EINE Rechnung über acht
              Touren. Die Nummer achtmal einzutippen ist nicht nur Arbeit, es
              ist die verlässlichste Art, sich beim sechsten Mal zu vertippen —
              und danach hängt eine Tour an einer Rechnung, die es nicht gibt.

              Die Datenbank setzt sie in EINER Anweisung: entweder alle oder
              keiner. Kommt eine Tour nicht durch, wird nichts gespeichert. */}
          {!eigenschaften.abgerechnet && offeneIds.length > 0 && (
            <Knopf
              art="haupt"
              klein
              disabled={ohnePreis > 0}
              title={
                ohnePreis > 0
                  ? 'Erst bei allen Touren Preis und Menge eintragen.'
                  : `Setzt eine Rechnungsnummer für alle ${offeneIds.length} Touren dieses Blocks.`
              }
              onClick={() => setBlockOffen((o) => !o)}
            >
              {blockOffen ? 'Abbrechen' : `Alle ${offeneIds.length} abschließen`}
            </Knopf>
          )}
        </div>
      }
      blank
    >
      {blockOffen && (
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--linie-hell)' }}>
          <div className="stapel" style={{ maxWidth: 560 }}>
            <Textfeld
              marke={`Rechnungsnummer für alle ${offeneIds.length} Touren`}
              wert={blocknummer}
              setzen={setBlocknummer}
              autoFocus
              platzhalter="Nummer aus SevDesk"
              hinweis={`${euro(summe)} netto. Danach sind alle ${offeneIds.length} Touren zu — auch für den Fahrer.`}
            />
            <div className="reihe reihe--rechts">
              <Knopf art="still" onClick={() => setBlockOffen(false)}>
                Abbrechen
              </Knopf>
              <Knopf
                art="haupt"
                disabled={blocknummer.trim() === ''}
                onClick={() => {
                  eigenschaften.blockAbschliessen(offeneIds, blocknummer.trim());
                  setBlockOffen(false);
                  setBlocknummer('');
                }}
              >
                Alle abschließen
              </Knopf>
            </div>
          </div>
        </div>
      )}

      <div className="tabellenrahmen">
        <table className="tabelle">
          <thead>
            <tr>
              <th>Tour</th>
              <th>Erfasst</th>
              <th>Preis</th>
              <th className="tabelle__rechts">Betrag</th>
              <th className="tabelle__rechts">Rechnungsnummer</th>
            </tr>
          </thead>
          <tbody>
            {eigenschaften.zeilen.map((zeile) => (
              <Zeile
                key={zeile.id}
                zeile={zeile}
                abgerechnet={eigenschaften.abgerechnet}
                beschaeftigt={eigenschaften.beschaeftigt}
                oeffnen={eigenschaften.oeffnen}
                preisAendern={eigenschaften.preisAendern}
                nummerSetzen={eigenschaften.nummerSetzen}
              />
            ))}
          </tbody>
        </table>
      </div>
    </Karte>
  );
}

function Zeile(eigenschaften: {
  zeile: Auftragszeile;
  abgerechnet: boolean;
  beschaeftigt: string | null;
  oeffnen: (zeile: Auftragszeile) => void;
  preisAendern: (
    zeile: Auftragszeile,
    werte: {
      preisart?: string | null;
      preis_vk?: number | null;
      mengeneinheit?: string | null;
      abrechnungsmenge?: number | null;
    },
  ) => void;
  nummerSetzen: (zeile: Auftragszeile, nummer: string) => void;
}) {
  const { zeile } = eigenschaften;
  const laeuft = eigenschaften.beschaeftigt?.includes(zeile.id) ?? false;
  const [preis, setPreis] = useState(zahlAlsText(zeile.preis_vk));
  const [menge, setMenge] = useState(zahlAlsText(zeile.abrechnungsmenge));
  const [nummer, setNummer] = useState('');
  const [fragt, setFragt] = useState(false);
  const [notizOffen, setNotizOffen] = useState(false);
  const notiz = fahrernotiz(zeile);

  /**
   * Ein Mengenfeld gibt es nur bei „nach Menge" und nicht bei „je Fuhre": Dort
   * zählt die Fuhrenzahl, und ein Feld, das etwas anderes behauptet, wäre eine
   * zweite Wahrheit. Ein leeres `mengeneinheit` gilt als Tonne — so zeigt es
   * auch die Auswahl darunter an.
   */
  const mengeSichtbar = zeile.preisart === 'menge' && zeile.mengeneinheit !== 'fuhre';
  const einheit = zeile.mengeneinheit ?? 't';

  return (
    <>
    <tr>
      <td>
        <button className="zeilenlink" onClick={() => eigenschaften.oeffnen(zeile)}>
          {zeile.nummer ?? 'ohne Nummer'}
        </button>
        {/* Die Notiz des Fahrers, als Zeichen: Genau hier wird über den Betrag
            entschieden, und eine Standzeit gehört dazu. Der Text klappt UNTER
            der Zeile auf — die Zeilenhöhe bleibt gleich. Nie im
            Leistungsnachweis: Der geht an den Kunden. */}
        {notiz && (
          <Fahrernotizzeichen
            text={notiz}
            offen={notizOffen}
            umschalten={() => setNotizOffen((o) => !o)}
          />
        )}
        <div className="meta">
          {zeile.ladeort} → {zeile.entladeort}
        </div>
        {zeile.ladedatum && (
          <div className="meta">
            {new Date(zeile.ladedatum).toLocaleDateString('de-DE', {
              weekday: 'short',
              day: '2-digit',
              month: '2-digit',
            })}
          </div>
        )}
      </td>

      <td>
        {zeile.fuhren > 0 ? (
          <div>
            {zeile.fuhren} {zeile.fuhren === 1 ? 'Fuhre' : 'Fuhren'}
          </div>
        ) : (
          <span className="stumm">keine Fuhren</span>
        )}
        {zeile.menge !== null && (
          <div className="meta">
            {zeile.menge.toLocaleString('de-DE')} {zeile.mengeneinheit ?? ''}
          </div>
        )}
      </td>

      <td>
        {eigenschaften.abgerechnet ? (
          <span className="meta">
            {PREISARTTEXT[zeile.preisart ?? ''] ?? '—'}{' '}
            {zeile.preis_vk !== null ? euro(Number(zeile.preis_vk)) : ''}
          </span>
        ) : (
          <div className="stapel" style={{ gap: 6, minWidth: 180 }}>
            <select
              value={zeile.preisart ?? ''}
              disabled={laeuft}
              onChange={(e) =>
                eigenschaften.preisAendern(
                  zeile,
                  // Beim Umstellen auf „nach Menge" wird die Einheit GLEICH
                  // mitgeschrieben. Die Auswahl zeigt „je Tonne" als Vorgabe,
                  // aber in der Datenbank stand NULL — und dann fehlte die
                  // Einheit im Leistungsnachweis, obwohl der Bildschirm eine
                  // zeigte.
                  e.target.value === 'menge'
                    ? { preisart: 'menge', mengeneinheit: zeile.mengeneinheit ?? 't' }
                    : { preisart: e.target.value || null },
                )
              }
            >
              <option value="">Preisart …</option>
              {Object.entries(PREISARTTEXT).map(([wert, text]) => (
                <option key={wert} value={wert}>
                  {text}
                </option>
              ))}
            </select>
            <Zahlfeld
              wert={preis}
              setzen={setPreis}
              platzhalter="Netto"
              beschriftung="Preis netto"
              disabled={laeuft}
              // Steht dort Unsinn, wird NICHT gespeichert — „leer" hieße sonst:
              // Preis löschen, und ein falsch gelesener Wert stünde auf der
              // Rechnung (siehe `daten/zahl.ts`).
              verlassen={(zahl) => {
                if (zahl.art === 'ungueltig') return;
                const wert = zahl.art === 'leer' ? null : zahl.wert;
                if (wert === zeile.preis_vk) return;
                eigenschaften.preisAendern(zeile, { preis_vk: wert });
              }}
            />
            {/* Bei „nach Menge" entscheidet die Einheit über den Betrag:
                `fuhre` rechnet mit der Fuhrenzahl, alles andere mit der
                Menge. Ohne dieses Feld stünde bei einem Preis je Fuhre der
                Tonnenbetrag auf der Rechnung — und zwar unauffällig. */}
            {zeile.preisart === 'menge' && (
              <select
                value={zeile.mengeneinheit ?? 't'}
                disabled={laeuft}
                onChange={(e) =>
                  eigenschaften.preisAendern(zeile, {
                    mengeneinheit: e.target.value,
                  })
                }
              >
                <option value="t">je Tonne</option>
                <option value="m3">je m³</option>
                <option value="fuhre">je Fuhre</option>
                <option value="stueck">je Stück</option>
              </select>
            )}
            {/* ═══ DIE MENGE FEHLTE — DARAN LAG „ICH KANN KEINEN PREIS FESTLEGEN" ═══

                Der Preis stand in der Datenbank. Aber der Betrag ist Preis mal
                MENGE, und die kam bisher nur aus den Fuhrenblöcken; ein
                Standardauftrag hat keine. Also blieb der Betrag „—", und es sah
                aus, als ginge der Preis nicht. Hier trägt das Büro die Menge
                ein, nach der abgerechnet wird — in der Regel Tonnen laut
                Wiegeschein. */}
            {mengeSichtbar && (
              <label className="feld">
                <span className="feld__marke">{mengenmarke(einheit)}</span>
                <Zahlfeld
                  wert={menge}
                  setzen={setMenge}
                  platzhalter={
                    zeile.menge !== null && zeile.abrechnungsmenge === null
                      ? zahlAlsText(zeile.menge)
                      : undefined
                  }
                  beschriftung={mengenmarke(einheit)}
                  disabled={laeuft}
                  verlassen={(zahl) => {
                    if (zahl.art === 'ungueltig') return;
                    const wert = zahl.art === 'leer' ? null : zahl.wert;
                    if (wert === zeile.abrechnungsmenge) return;
                    eigenschaften.preisAendern(zeile, {
                      abrechnungsmenge: wert,
                      // Steht noch keine Einheit in der Datenbank, bekommt sie
                      // die Menge jetzt mit — sonst fehlt sie im Nachweis.
                      ...(zeile.mengeneinheit === null ? { mengeneinheit: 't' } : {}),
                    });
                  }}
                />
              </label>
            )}
          </div>
        )}
      </td>

      <td className="tabelle__rechts" style={{ fontWeight: 600 }}>
        {euro(betrag(zeile))}
        {zeile.preisart === 'stundensatz' && (
          <div className="meta" style={{ fontWeight: 400 }}>
            Stunden stehen am Arbeitstag
          </div>
        )}
      </td>

      <td className="tabelle__rechts">
        {zeile.rechnungsnummer ? (
          <strong>{zeile.rechnungsnummer}</strong>
        ) : fragt ? (
          <div className="stapel" style={{ gap: 6, minWidth: 210 }}>
            <input
              type="text"
              value={nummer}
              placeholder="Nummer aus SevDesk"
              autoFocus
              disabled={laeuft}
              onChange={(e) => setNummer(e.target.value)}
            />
            <span className="meta" style={{ textAlign: 'left' }}>
              Danach ist die Tour zu — auch für den Fahrer.
            </span>
            <div className="reihe reihe--rechts" style={{ gap: 6 }}>
              <Knopf art="still" klein onClick={() => setFragt(false)}>
                Abbrechen
              </Knopf>
              <Knopf
                art="haupt"
                klein
                disabled={laeuft || nummer.trim() === ''}
                onClick={() => eigenschaften.nummerSetzen(zeile, nummer)}
              >
                Eintragen
              </Knopf>
            </div>
          </div>
        ) : (
          <Knopf
            art="zweit"
            klein
            disabled={laeuft || ohneBetrag(zeile)}
            title={
              ohneBetrag(zeile)
                ? zeile.preis_vk === null || zeile.preisart === null
                  ? 'Erst den Preis eintragen — sonst steht auf der Rechnung nichts.'
                  : 'Erst die Menge eintragen — sonst steht auf der Rechnung kein Betrag.'
                : undefined
            }
            onClick={() => setFragt(true)}
          >
            Eintragen
          </Knopf>
        )}
      </td>
    </tr>
    {notiz && notizOffen && (
      <tr className="tabelle__notizzeile">
        <td colSpan={5}>
          <Fahrernotizblock zeilen={notiz.split('\n')} />
        </td>
      </tr>
    )}
    </>
  );
}
